// ─────────────────────────────────────────────────────────────────────────────
// ProjectAdminSection — admin-only master register of every project.
//
// A searchable, filterable table of all projects with their status and headline
// commercials. This is where projects are CREATED and DELETED (the Home page now
// only edits the currently-selected project). Lives under the "Admin Tools" nav
// group, so it is only visible to administrators.
//
// Add / Edit reuse the existing Home edit form (beginAddProject / select+edit),
// so capture happens in one place. Delete is guarded by a confirm.
// ─────────────────────────────────────────────────────────────────────────────

import React, { memo, useMemo, useState } from "react";
import { currency } from "../utils/formatters";
import { tokens } from "./theme";
import {
  PageHeader, Toolbar, Table, Th, Td, Badge, Button, Input, Select, Empty,
} from "./ui";

const t = tokens;

// Status → Badge variant
function statusVariant(status) {
  if (status === "Active") return "success";
  if (status === "Closed") return "default";
  if (status === "Planning") return "warning";
  return "default";
}

const I = {
  add:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>,
  open:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  search: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
};

function ProjectAdminSection({
  projects = [],
  selectedProject,
  onSelect,
  onAdd,
  onEdit,
  onDelete,
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter((p) => {
      if (statusFilter !== "All" && (p.status || "") !== statusFilter) return false;
      if (!q) return true;
      const haystack = [
        p.id, p.name, p.client, p.department,
        p.contractNumber || p.contractNo, p.contractor, p.status,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [projects, search, statusFilter]);

  const counts = useMemo(() => {
    const c = { total: projects.length, Active: 0, Planning: 0, Closed: 0 };
    projects.forEach((p) => { if (c[p.status] != null) c[p.status] += 1; });
    return c;
  }, [projects]);

  const handleDelete = (project) => {
    const ok = window.confirm(
      `Delete project ${project.id} — ${project.name || "Untitled"}?\n\n` +
      `This removes the project record. This action cannot be undone.`
    );
    if (ok) onDelete(project.id);
  };

  return (
    <section style={{ display: "grid", gap: 16, fontFamily: t.font.body }}>
      <PageHeader
        eyebrow="Admin Tools"
        title="Project Register"
        subtitle="The master list of every project. Search, review status, and create or remove project records here. Open a project to work in it, or edit its contract details."
        badges={
          <>
            <Badge variant="primary">{counts.total} total</Badge>
            <Badge variant="success">{counts.Active} active</Badge>
            <Badge variant="warning">{counts.Planning} planning</Badge>
            <Badge variant="default">{counts.Closed} closed</Badge>
          </>
        }
        actions={
          <Button variant="accent" icon={I.add} onClick={onAdd}>
            Add Project
          </Button>
        }
      />

      <Toolbar>
        <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
          <span style={{
            position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            color: t.color.inkSubtle, display: "flex", pointerEvents: "none",
          }}>{I.search}</span>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by number, name, client or contract…"
            style={{ paddingLeft: 36 }}
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ width: "auto", minWidth: 150 }}
        >
          <option value="All">All statuses</option>
          <option value="Planning">Planning</option>
          <option value="Active">Active</option>
          <option value="Closed">Closed</option>
        </Select>
        <span style={{ fontSize: 12, color: t.color.inkMuted, fontWeight: 600 }}>
          {filtered.length} shown
        </span>
      </Toolbar>

      {filtered.length === 0 ? (
        <Empty
          icon="◈"
          title="No projects match"
          message={
            projects.length === 0
              ? "No projects have been created yet. Use Add Project to create the first one."
              : "Try a different search term or clear the status filter."
          }
          action={
            <Button variant="accent" icon={I.add} onClick={onAdd}>Add Project</Button>
          }
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th sticky width={120}>Project No</Th>
              <Th sticky>Name</Th>
              <Th sticky>Client</Th>
              <Th sticky width={150}>Contract No</Th>
              <Th sticky width={110}>Status</Th>
              <Th sticky align="right" width={150}>Grand Total</Th>
              <Th sticky align="right" width={230}>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const isCurrent = p.id === selectedProject;
              return (
                <tr
                  key={p.id}
                  style={isCurrent ? { background: t.color.primarySoft } : undefined}
                >
                  <Td mono weight={700}>{p.id}</Td>
                  <Td weight={600}>{p.name || "—"}</Td>
                  <Td>{p.client || "—"}</Td>
                  <Td mono>{p.contractNumber || p.contractNo || "—"}</Td>
                  <Td>
                    <Badge variant={statusVariant(p.status)} size="sm">
                      {p.status || "—"}
                    </Badge>
                  </Td>
                  <Td mono align="right">{currency(p.grandTotal)}</Td>
                  <Td align="right">
                    <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                      <Button size="sm" variant="outline" icon={I.open} onClick={() => onSelect(p.id)}>
                        Open
                      </Button>
                      <Button size="sm" variant="outline" icon={I.edit} onClick={() => onEdit(p.id)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="danger" icon={I.trash} onClick={() => handleDelete(p)}>
                        Delete
                      </Button>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </section>
  );
}

export default memo(ProjectAdminSection);
